import type React from 'react';
import { memo, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

export interface PortalProps {
  id: string;
  preserveElement?: boolean;
  children: React.ReactNode;
}

const Portal = ({ id, preserveElement, children }: PortalProps) => {
  const [el, setEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const existingElement = document.getElementById(id);

    if (existingElement) {
      setEl(existingElement);
      return;
    }

    // Delay checking for the element after 100ms if it doesn't exist in initial
    const timeout = setTimeout(() => {
      const newElement = document.getElementById(id);
      if (newElement) {
        setEl(newElement);
      } else {
        const createNewElement = document.createElement('div');
        createNewElement.id = id;
        setEl(createNewElement);

        if (document.body) {
          document.body.appendChild(createNewElement);
        }
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
      if (!preserveElement && el?.parentElement) {
        el.parentElement.removeChild(el);
      }
    };
  }, [id, el]);

  if (!el) {
    return null;
  }

  return ReactDOM.createPortal(children, el);
};

export default memo(Portal);
